/**
 * Busca Server-Side Genérica para SmartSelect
 *
 * Este utilitário fornece busca server-side para entidades configuradas
 * no dropdownConfig.js. Funciona de duas formas:
 *
 * 1. Se a entidade tem RPC configurada → usa a RPC
 * 2. Se não tem RPC → usa fallback com ILIKE genérico
 *
 * @see dropdownConfig.js - Configuração de entidades
 * @see SmartSelect.jsx - Componente que usa este utilitário
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  getEntityConfig,
  DEFAULT_SEARCH_LIMIT
} from '@/config/dropdownConfig';
import { normalizeText } from '@/utils/fetchAllRecords';

type Row = Record<string, unknown>;

type DefaultFilter = {
  column: string;
  value: unknown;
  operator?: 'eq' | 'neq' | 'is';
};

type SignedUrlConfig = {
  field: string;
  urlField: string;
  bucket: string;
};

type EntityConfig = {
  rpc?: string;
  rpcParams?: (query: string, opts: Record<string, unknown>) => Record<string, unknown>;
  table?: string;
  select?: string;
  orderBy?: string;
  defaultFilters?: DefaultFilter[];
  searchFields?: string[];
  labelFn?: ((row: Row) => string) | string;
  mapResult?: (row: Row) => Row;
  generateSignedUrls?: SignedUrlConfig;
  optionFilters?: (options: GenericSearchOptions) => DefaultFilter[];
};

type GenericSearchOptions = {
  limit?: number;
  recurso?: string;
  [key: string]: unknown;
};

export type DropdownOption = Row & {
  value: unknown;
  label: string;
  searchText?: string;
};

// Supabase strict-typed client causes .from(dynamicTable) overload resolution
// to blow up; cast to untyped here and rely on runtime config validation.
const db: SupabaseClient = supabase as unknown as SupabaseClient;

/**
 * Executa busca server-side para uma entidade
 *
 * @param {string} entity - Nome da entidade (deve existir em dropdownConfig)
 * @param {string} query - Texto de busca
 * @param {Object} options - Opções adicionais
 * @param {number} options.limit - Limite de resultados (default: 50)
 * @param {string} options.recurso - Recurso para RPCs com controle de acesso
 * @returns {Promise<Array>} Array de opções {value, label, ...dados}
 *
 * @example
 * // Busca simples
 * const cidades = await genericServerSearch('cidade', 'são paulo');
 *
 * // Com opções
 * const pontos = await genericServerSearch('obra_ponto', 'sala', { recurso: 'OBRA_PRODUTO' });
 */
export const genericServerSearch = async (
  entity: string,
  query = '',
  options: GenericSearchOptions = {}
): Promise<DropdownOption[]> => {
  const config = getEntityConfig(entity) as EntityConfig | null | undefined;

  if (!config) {
    console.error(`[genericServerSearch] Entidade desconhecida: ${entity}`);
    return [];
  }

  const { limit = DEFAULT_SEARCH_LIMIT } = options;

  // =========================================================================
  // ESTRATÉGIA 1: Usar RPC dedicada (se configurada)
  // =========================================================================
  if (config.rpc) {
    try {
      const params = config.rpcParams
        ? config.rpcParams(query, { ...options, limit })
        : { p_search: query, p_limit: limit };

      const { data, error } = await db.rpc(config.rpc, params);

      if (error) {
        console.error(`[genericServerSearch] Erro na RPC ${config.rpc}:`, error);
        // Se RPC falhar, tenta fallback genérico
        if (config.table) {
          console.warn(`[genericServerSearch] Tentando fallback para ${entity}`);
          return genericFallbackSearch(config, query, limit, options);
        }
        return [];
      }

      // Mapear resultados se tiver função de mapeamento
      if (config.mapResult && Array.isArray(data)) {
        let results = (data as Row[]).map(config.mapResult) as DropdownOption[];
        // Gerar signed URLs para fotos (ex: produto.foto_1)
        if (config.generateSignedUrls) {
          results = await resolveSignedUrls(results, config.generateSignedUrls);
        }
        return results;
      }

      return (data as DropdownOption[]) ?? [];
    } catch (err) {
      console.error(`[genericServerSearch] Exceção na RPC ${config.rpc}:`, err);
      // Tenta fallback
      if (config.table) {
        return genericFallbackSearch(config, query, limit, options);
      }
      return [];
    }
  }

  // =========================================================================
  // ESTRATÉGIA 2: Fallback genérico com ILIKE
  // =========================================================================
  return genericFallbackSearch(config, query, limit, options);
};

/**
 * Gera signed URLs para campos de foto nos resultados
 * @param {Array} results - Resultados mapeados
 * @param {Object} config - { field, urlField, bucket }
 * @returns {Promise<Array>} Resultados com URLs preenchidas
 */
const resolveSignedUrls = async (
  results: DropdownOption[],
  config: SignedUrlConfig
): Promise<DropdownOption[]> => {
  const { field, urlField, bucket } = config;
  const withPhotos = results.filter(r => {
    const v = r[field];
    return typeof v === 'string' && v.trim().length > 0;
  });
  if (withPhotos.length === 0) return results;

  await Promise.all(withPhotos.map(async (r) => {
    try {
      const path = r[field] as string;
      const { data } = await db.storage
        .from(bucket)
        .createSignedUrl(path, 3600);
      if (data?.signedUrl) {
        r[urlField] = data.signedUrl;
      }
    } catch {
      // Silenciosamente ignora — foto simplesmente não aparece
    }
  }));

  return results;
};

/**
 * Busca genérica com ILIKE para entidades sem RPC
 *
 * @param {Object} config - Configuração da entidade
 * @param {string} query - Texto de busca
 * @param {number} limit - Limite de resultados
 * @returns {Promise<Array>} Array de opções
 */
const genericFallbackSearch = async (
  config: EntityConfig,
  query: string,
  limit: number,
  options: GenericSearchOptions = {}
): Promise<DropdownOption[]> => {
  if (!config.table) {
    console.error('[genericFallbackSearch] Configuração sem table definida');
    return [];
  }

  try {
    let queryBuilder = db
      .from(config.table)
      .select(config.select || '*')
      .order(config.orderBy || 'id')
      .limit(limit);

    const extraFilters = [
      ...(config.defaultFilters || []),
      ...(config.optionFilters?.(options) || []),
    ];
    for (const filter of extraFilters) {
      const operator = filter.operator || 'eq';
      if (operator === 'is') {
        queryBuilder = queryBuilder.is(filter.column, filter.value);
      } else if (operator === 'eq') {
        queryBuilder = queryBuilder.eq(filter.column, filter.value);
      } else if (operator === 'neq') {
        queryBuilder = queryBuilder.neq(filter.column, filter.value);
      }
    }

    // Aplicar busca se query fornecida
    const searchFields = config.searchFields;
    if (query && query.trim() && searchFields && searchFields.length > 0) {
      const searchTerm = query.trim().toLowerCase();

      // Criar condições OR para cada campo de busca
      // Nota: campos aninhados (ex: 'estado.uf') não funcionam com .or()
      // Filtramos apenas campos diretos
      const directFields = searchFields.filter((f: string) => !f.includes('.'));

      if (directFields.length > 0) {
        const orConditions = directFields
          .map(field => `${field}.ilike.%${searchTerm}%`)
          .join(',');
        queryBuilder = queryBuilder.or(orConditions);
      }
    }

    const { data, error } = await queryBuilder;

    if (error) {
      console.error(`[genericFallbackSearch] Erro na query para ${config.table}:`, error);
      return [];
    }

    // Mapear resultados para formato de opção
    return ((data as unknown as Row[]) ?? []).map((row) => mapRowToOption(row, config));
  } catch (err) {
    console.error(`[genericFallbackSearch] Exceção para ${config.table}:`, err);
    return [];
  }
};

/**
 * Mapeia um registro para o formato de opção do dropdown
 *
 * @param {Object} row - Registro do banco
 * @param {Object} config - Configuração da entidade
 * @returns {Object} Opção {value, label, searchText, ...row}
 */
const mapRowToOption = (row: Row, config: EntityConfig): DropdownOption => {
  // Determinar o label
  let label: string;
  if (typeof config.labelFn === 'function') {
    label = config.labelFn(row);
  } else if (typeof config.labelFn === 'string') {
    label = String(row[config.labelFn] ?? '');
  } else {
    label = String(row.id ?? '');
  }

  // Criar searchText para busca client-side (se necessário no futuro)
  let searchText = label;
  if (config.searchFields && config.searchFields.length > 0) {
    const searchParts = config.searchFields
      .map((field: string) => {
        if (field.includes('.')) {
          // Campo aninhado
          const parts = field.split('.');
          let value: unknown = row;
          for (const part of parts) {
            value = value && typeof value === 'object' ? (value as Row)[part] : undefined;
            if (value === undefined || value === null) break;
          }
          return value;
        }
        return row[field];
      })
      .filter(Boolean);
    searchText = normalizeText(searchParts.join(' '));
  }

  return {
    ...row,
    value: row.id,
    label,
    searchText,
  };
};

/**
 * Carrega todos os registros de uma entidade (para client-side)
 * Usa paginação para superar limite de 1000 do Supabase
 *
 * @param {string} entity - Nome da entidade
 * @param {Object} options - Opções adicionais
 * @returns {Promise<Array>} Array de opções
 */
export const loadAllEntityOptions = async (
  entity: string,
  options: GenericSearchOptions & { pageSize?: number } = {}
): Promise<DropdownOption[]> => {
  const config = getEntityConfig(entity) as EntityConfig | null | undefined;

  if (!config) {
    console.error(`[loadAllEntityOptions] Entidade desconhecida: ${entity}`);
    return [];
  }

  if (!config.table) {
    console.error(`[loadAllEntityOptions] Entidade ${entity} não tem table configurada`);
    return [];
  }

  const { pageSize = 500 } = options;
  let page = 0;
  let allRecords: Row[] = [];

  try {
    while (true) {
      const from = page * pageSize;
      const to = from + pageSize - 1;

      let queryBuilder = db
        .from(config.table)
        .select(config.select || '*')
        .order(config.orderBy || 'id')
        .range(from, to);

      const extraFilters = [
        ...(config.defaultFilters || []),
        ...(config.optionFilters?.(options) || []),
      ];
      for (const filter of extraFilters) {
        const operator = filter.operator || 'eq';
        if (operator === 'is') {
          queryBuilder = queryBuilder.is(filter.column, filter.value);
        } else if (operator === 'eq') {
          queryBuilder = queryBuilder.eq(filter.column, filter.value);
        } else if (operator === 'neq') {
          queryBuilder = queryBuilder.neq(filter.column, filter.value);
        }
      }

      const { data, error } = await queryBuilder;

      if (error) {
        console.error(`[loadAllEntityOptions] Erro na página ${page}:`, error);
        break;
      }

      const rows = (data as unknown as Row[]) ?? [];
      if (rows.length === 0) break;

      allRecords = allRecords.concat(rows);

      // Se retornou menos que pageSize, é a última página
      if (rows.length < pageSize) break;

      page++;
    }

    // Mapear todos os registros para opções
    return allRecords.map(row => mapRowToOption(row, config));
  } catch (err) {
    console.error(`[loadAllEntityOptions] Exceção para ${entity}:`, err);
    return [];
  }
};

export default genericServerSearch;
